import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Breadcrumbs, organizationLd, webSiteLd, datasetLd } from '../seo';
import cities from '../data/cities.json';

interface CityPrice {
  city: string;
  slug: string;
  avg_monthly: number | null;
  count: number;
}

export default function Prices() {
  const [cheapest, setCheapest] = useState<CityPrice[]>([]);
  const [expensive, setExpensive] = useState<CityPrice[]>([]);
  const [updatedAt, setUpdatedAt] = useState<string>('');

  useEffect(() => {
    Promise.all([
      fetch('/api/stats/leaderboard?sort=cheapest&min_count=1&limit=20').then((r) => r.json()),
      fetch('/api/stats/leaderboard?sort=expensive&min_count=1&limit=20').then((r) => r.json()),
    ])
      .then(([c, e]) => {
        if (Array.isArray(c?.data?.results)) setCheapest(c.data.results);
        if (Array.isArray(e?.data?.results)) setExpensive(e.data.results);
      })
      .catch(() => undefined);
    setUpdatedAt(new Date().toISOString());
  }, []);

  return (
    <>
      <Breadcrumbs
        items={[{ label: 'Home', to: '/' }, { label: 'Parking prices in India' }]}
        pageTitle="Parking Prices in India — Live Crowdsourced Data | Parksy"
        pageDescription="Real, crowdsourced parking prices across 12 Indian cities. See what people actually pay for monthly, daily, and hourly parking in any city or locality."
        extraSchemas={[
          organizationLd(),
          webSiteLd(),
          datasetLd({
            name: 'India parking prices',
            description: 'Live, crowdsourced parking prices across 12 Indian cities.',
            url: 'https://parksy.rent/prices',
            citySlug: 'india',
            updatedAt,
          }),
        ]}
      />
      <div className="max-w-5xl mx-auto px-4 py-10">
        <header className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Parking prices in India</h1>
          <p className="text-lg text-gray-700">
            Real rates, crowdsourced. No quotes, no broker padding. Pick a city to see monthly,
            daily, and hourly averages, plus the most- and least-expensive localities.
          </p>
        </header>

        <section className="mb-10">
          <h2 className="text-2xl font-semibold text-gray-900 mb-3">Pick a city</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 text-sm">
            {cities.map((c) => (
              <Link
                key={c.slug}
                to={`/prices/${c.slug}`}
                className="bg-white border rounded-lg px-3 py-2 text-gray-700 hover:border-parksy-500 hover:text-parksy-700"
              >
                {c.name} prices
              </Link>
            ))}
          </div>
        </section>

        <section className="mb-10">
          <h2 className="text-2xl font-semibold text-gray-900 mb-3">Cheapest metros</h2>
          <PriceTable rows={cheapest} fallback="Leaderboard populates as listings grow." />
        </section>

        <section className="mb-10">
          <h2 className="text-2xl font-semibold text-gray-900 mb-3">Most expensive metros</h2>
          <PriceTable rows={expensive} fallback="Leaderboard populates as listings grow." />
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mb-3">How this data is collected</h2>
          <p className="text-gray-700 text-sm mb-3">
            Anyone can drop a "price pin" on the map — what they currently pay, anonymously.
            Parksy aggregates those pins by city and locality, filters outliers, and surfaces
            medians, p25, p75, and averages. No personal data is exposed.
          </p>
          <Link
            to="/pin-your-rent"
            className="text-parksy-700 hover:underline text-sm"
          >
            Pin what you pay →
          </Link>
        </section>
      </div>
    </>
  );
}

function PriceTable({ rows, fallback }: { rows: CityPrice[]; fallback: string }) {
  if (rows.length === 0) {
    return (
      <div className="bg-white border rounded-xl p-6 text-sm text-gray-500 text-center">
        {fallback}
      </div>
    );
  }
  return (
    <div className="bg-white border rounded-xl overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th className="text-left px-4 py-2">City</th>
            <th className="text-right px-4 py-2">Listings</th>
            <th className="text-right px-4 py-2">Avg ₹/month</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.slug} className="border-t">
              <td className="px-4 py-2 font-medium text-gray-900">
                <Link to={`/prices/${row.slug}`} className="hover:text-parksy-600">
                  {row.city}
                </Link>
              </td>
              <td className="px-4 py-2 text-right text-gray-600">{row.count}</td>
              <td className="px-4 py-2 text-right text-gray-600">
                {row.avg_monthly ? `₹${Math.round(row.avg_monthly).toLocaleString('en-IN')}` : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
